import { ensureCustomFilterStyle } from './filterStyles';

/**
 * 세대수 범위 필터
 * 사용 예: { filter: HouseholdRangeFilter }
 */
class HouseholdRangeFilter {
  init(params) {
    ensureCustomFilterStyle();
    this.params = params;
    this.model = null;
    this.onClose = params.onClose || null;
    this.gui = document.createElement('div');
    this.gui.className = 'aptrank-custom-filter';
    this.render();
  }

  render() {
    const from = this.model?.min ?? '';
    const to = this.model?.max ?? '';
    // 설정값이 없으면 슬라이더는 전체 범위(min=좌측끝, max=우측끝)로 배치해 두 핸들이 겹치지 않도록.
    // Number('')는 0(유한값)이라 단순 isFinite 검사로는 빈 값 판별이 안 됨 → 빈 문자열 명시 처리.
    const sliderMin = from === '' ? 0 : Math.max(0, Math.min(2000, Number(from) || 0));
    const sliderMax = to === '' ? 2000 : Math.max(0, Math.min(2000, Number(to) || 2000));
    this.gui.innerHTML = `
      <button type="button" class="complex-top-filter-panel-close" style="position: absolute; top: 8px; right: 8px; border: none; background: transparent; color: #64748b; width: 28px; height: 28px; border-radius: 8px; font-size: 24px; line-height: 1; cursor: pointer; z-index: 10;">×</button>
      <div class="aptrank-custom-filter-head">
        <div class="aptrank-custom-filter-heading">세대수</div>
      </div>
      <div class="aptrank-custom-filter-title-edit">
        <input class="title-min" inputmode="numeric" placeholder="최소" value="${from}" />
        <span>~</span>
        <input class="title-max" inputmode="numeric" placeholder="최대" value="${to}" />
      </div>
      <div class="aptrank-custom-filter-slider-wrap">
        <div class="aptrank-custom-filter-slider-track"></div>
        <input class="aptrank-custom-filter-slider min-slider" type="range" min="0" max="2000" step="10" value="${Math.max(0, Math.min(2000, sliderMin))}" />
        <input class="aptrank-custom-filter-slider max-slider" type="range" min="0" max="2000" step="10" value="${Math.max(0, Math.min(2000, sliderMax))}" />
      </div>
      <div class="aptrank-custom-filter-grid" style="grid-template-columns: repeat(5, minmax(0, 1fr));">
        <button class="aptrank-custom-filter-chip" data-range="0-200">~200</button>
        <button class="aptrank-custom-filter-chip" data-range="200-99999">200~</button>
        <button class="aptrank-custom-filter-chip" data-range="300-99999">300~</button>
        <button class="aptrank-custom-filter-chip" data-range="500-99999">500~</button>
        <button class="aptrank-custom-filter-chip" data-range="1000-99999">1000~</button>
      </div>
      <div class="aptrank-custom-filter-actions">
        <button class="aptrank-custom-filter-reset" type="button">초기화</button>
        <button class="aptrank-custom-filter-apply" type="button">적용</button>
      </div>
    `;

    const applyFilter = () => {
      const minRaw = this.gui.querySelector('input.title-min').value.replace(/\D/g, '');
      const maxRaw = this.gui.querySelector('input.title-max').value.replace(/\D/g, '');
      if (!minRaw && !maxRaw) {
        this.model = null;
      } else {
        const min = minRaw ? Number(minRaw) : 0;
        const max = maxRaw ? Number(maxRaw) : Number.POSITIVE_INFINITY;
        this.model = { min, max };
      }
      this.params.filterChangedCallback();
    };

    const updateSlidersFromInputs = () => {
      const minInput = this.gui.querySelector('input.title-min');
      const maxInput = this.gui.querySelector('input.title-max');
      const minSlider = this.gui.querySelector('input.min-slider');
      const maxSlider = this.gui.querySelector('input.max-slider');

      const minVal = minInput.value.replace(/\D/g, '');
      const maxVal = maxInput.value.replace(/\D/g, '');

      if (minVal) {
        const minNum = Math.min(Number(minVal), 2000);
        minSlider.value = minNum;
      }
      if (maxVal) {
        const maxNum = Math.min(Number(maxVal), 2000);
        maxSlider.value = maxNum;
      }
    };

    const updateTrack = () => {
      const minSlider = this.gui.querySelector('input.min-slider');
      const maxSlider = this.gui.querySelector('input.max-slider');
      const track = this.gui.querySelector('.aptrank-custom-filter-slider-track');
      if (!track || !minSlider || !maxSlider) return;

      const minVal = Number(minSlider.value);
      const maxVal = Number(maxSlider.value);
      const leftPercent = (minVal / 2000) * 100;
      const rightPercent = 100 - (maxVal / 2000) * 100;

      track.style.left = `${leftPercent}%`;
      track.style.right = `${rightPercent}%`;
    };

    // 슬라이더 변경 시 입력 필드 및 트랙 업데이트 (적용은 버튼으로)
    this.gui.querySelector('input.min-slider').addEventListener('input', (e) => {
      const next = Number(e.target.value);
      const maxText = this.gui.querySelector('input.title-max').value.replace(/\D/g, '');
      const max = maxText ? Number(maxText) : 2000;
      this.gui.querySelector('input.title-min').value = String(Math.min(next, max));
      updateTrack();
    });
    this.gui.querySelector('input.max-slider').addEventListener('input', (e) => {
      const next = Number(e.target.value);
      const minText = this.gui.querySelector('input.title-min').value.replace(/\D/g, '');
      const min = minText ? Number(minText) : 0;
      this.gui.querySelector('input.title-max').value = String(Math.max(next, min));
      updateTrack();
    });

    // 입력 필드 변경 시 슬라이더 및 트랙 업데이트 (적용은 버튼으로)
    this.gui.querySelector('input.title-min').addEventListener('input', () => {
      updateSlidersFromInputs();
      updateTrack();
    });
    this.gui.querySelector('input.title-max').addEventListener('input', () => {
      updateSlidersFromInputs();
      updateTrack();
    });

    // 초기 트랙 위치 설정
    updateTrack();

    // 버튼 클릭 시 값만 설정 (적용 버튼 눌러야 적용됨)
    this.gui.querySelectorAll('[data-range]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const [minStr, maxStr] = String(btn.getAttribute('data-range') || '').split('-');
        const min = Number(minStr || 0);
        const max = Number(maxStr || 99999);
        this.gui.querySelector('input.title-min').value = min;
        this.gui.querySelector('input.title-max').value = max;
        this.gui.querySelector('input.min-slider').value = Math.min(min, 2000);
        this.gui.querySelector('input.max-slider').value = Math.min(max, 2000);
        updateTrack();
      });
    });

    // 적용 버튼
    this.gui.querySelector('.aptrank-custom-filter-apply').addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      applyFilter();
    });

    // 초기화 버튼
    this.gui.querySelector('.aptrank-custom-filter-reset').addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.gui.querySelector('input.title-min').value = '';
      this.gui.querySelector('input.title-max').value = '';
      this.gui.querySelector('input.min-slider').value = 0;
      this.gui.querySelector('input.max-slider').value = 2000;
      updateTrack();
      this.model = null;
      this.params.filterChangedCallback();
    });

    const closeBtn = this.gui.querySelector('.complex-top-filter-panel-close');
    if (closeBtn && this.onClose) {
      closeBtn.addEventListener('click', this.onClose);
    }
  }

  getGui() {
    return this.gui;
  }

  isFilterActive() {
    return !!this.model;
  }

  doesFilterPass(p) {
    if (!this.model) return true;
    const value = Number(p?.data?.familyCnt || 0);
    if (!Number.isFinite(value)) return false;
    return value >= this.model.min && value <= this.model.max;
  }

  getModel() {
    return this.model;
  }

  setModel(model) {
    this.model = model || null;
    if (this.gui) this.render();
  }
}

export default HouseholdRangeFilter;
